import React from "react";
import { useTranslation } from "react-i18next";
import Modal from "@/components/controls/Modal";
import Text from "@/components/ui/text";
import { Button } from "@/components/ui/button";
import IconContainer from "@/components/ui/icon-container";
import {
  ArrowRight,
  GuardrailsModalIllustration,
  Workflow
} from "@dreamhub/designsystem/icons";
import { GuardrailsErrorResponse } from "./types";
import { DealScoreBadge } from "@/pages/deals/components/DealScoreBadge";
import { ScoreType } from "@/pages/deals/scoreConfig";
import { DealData } from "@/pages/deals/types";
import { useDealsNavigation } from "@/services/hooks/entities/deal/useDealsNavigation";
import { useDealStages } from "@/entities/settings/deal/hooks";
import {
  TabOption,
  TAB_SEARCH_PARAM,
  getTabSlug
} from "@/components/layouts/DetailedView";
import { useUpdateDealStageMutation, useResetDeal } from "@/pages/deals/hooks";
import {
  isCompletedWorkflowsError,
  getCleanedErrorMessage,
  getNextStage,
  parseErrorData
} from "./helpers";

interface Props {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  dealId: string | null;
  deal?: DealData | null;
  errorData: GuardrailsErrorResponse | string | null;
}

const GuardrailsFailureModal: React.FC<Props> = ({
  isOpen,
  setIsOpen,
  dealId,
  deal,
  errorData
}) => {
  const { t } = useTranslation();
  const { navigateToDetailedView } = useDealsNavigation();
  const { data: stagesData } = useDealStages();
  const updateDealStageMutation = useUpdateDealStageMutation();
  const resetDeal = useResetDeal(dealId || "");

  const dealData: DealData | null = deal || null;
  const parsedErrorData = parseErrorData(errorData);
  const isCompletedWorkflowsErrorFlag = isCompletedWorkflowsError(errorData);
  const nextStage = getNextStage(dealData, stagesData?.stages);
  const cleanedErrorMessage = getCleanedErrorMessage(errorData);

  const handleUpdateWorkflows = () => {
    if (!dealId) return;

    if (isCompletedWorkflowsErrorFlag && nextStage) {
      updateDealStageMutation.mutate(
        {
          pathParams: {
            dealId,
            newStageId: nextStage.id
          },
          data: {}
        },
        {
          onSuccess: () => {
            resetDeal();
            setTimeout(() => {
              setIsOpen(false);
            }, 100);
          },
          onError: (error) => {
            console.error("Failed to update deal stage:", error);
            navigateToDetailedView(dealId, {
              [TAB_SEARCH_PARAM]: getTabSlug(TabOption.STAGE_WORKFLOWS)
            });
            setTimeout(() => {
              setIsOpen(false);
            }, 100);
          }
        }
      );
      return;
    }

    // Handle the regular workflow error case
    const firstFailedQuestionnaire =
      parsedErrorData?.failed_questionnaires?.[0];
    const questionnaireId = firstFailedQuestionnaire?.questionnaire_id;

    if (questionnaireId) {
      navigateToDetailedView(dealId, {
        [TAB_SEARCH_PARAM]: getTabSlug(TabOption.STAGE_WORKFLOWS),
        openQuestionnaire: questionnaireId
      });
    } else {
      navigateToDetailedView(dealId, {
        [TAB_SEARCH_PARAM]: getTabSlug(TabOption.STAGE_WORKFLOWS)
      });
    }

    setTimeout(() => {
      setIsOpen(false);
    }, 100);
  };

  const handleCancel = () => {
    setIsOpen(false);
  };

  return (
    <Modal
      open={isOpen}
      setIsOpen={setIsOpen}
      headerClassname="border-none"
      contentClassname="w-[640px]"
      data-test="deals-guardrails-modal"
      title={
        <div
          className="flex flex-col items-center gap-4 pt-4"
          data-test="deals-guardrails-modal-header"
        >
          <GuardrailsModalIllustration />
          <Text
            as="div"
            variant="lgSubtitleH1"
            className="text-center"
            data-test="deals-guardrails-modal-title"
          >
            {isCompletedWorkflowsErrorFlag
              ? t("guardrails.modal.skippingStepsTitle")
              : t("guardrails.modal.title")}
          </Text>
        </div>
      }
      content={
        <div
          className="flex flex-col gap-6 px-6 pb-4"
          data-test="deals-guardrails-modal-content"
        >
          {isCompletedWorkflowsErrorFlag ? (
            <Text as="div" variant="body" className="text-center">
              {cleanedErrorMessage}
            </Text>
          ) : (
            <>
              <Text as="div" variant="body" className="text-center">
                {parsedErrorData?.failed_questionnaires?.length === 1
                  ? t("guardrails.modal.workflowScoreMessage", {
                      questionnaireName:
                        parsedErrorData.failed_questionnaires[0]
                          .questionnaire_name
                    })
                  : (parsedErrorData?.failed_questionnaires?.length ?? 0) > 1
                    ? t("guardrails.modal.multipleWorkflowsMessage", {
                        count:
                          parsedErrorData?.failed_questionnaires?.length ?? 0
                      })
                    : t("guardrails.modal.defaultMessage")}
              </Text>

              <div className="flex flex-col gap-3 max-h-[40vh] overflow-y-auto">
                {parsedErrorData?.failed_questionnaires?.map(
                  (questionnaire, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-8 p-3 rounded-lg bg-surface-elevation2 border border-border-border1"
                    >
                      <div className="flex items-center">
                        <IconContainer size="xl" color="primary">
                          <Workflow />
                        </IconContainer>
                        <Text
                          variant="body"
                          className="truncate max-w-[230px]"
                          hasTruncatedTooltip
                        >
                          {questionnaire.questionnaire_name}
                        </Text>
                      </div>
                      {questionnaire.kpi_value === ScoreType.GENERAL ? (
                        <>
                          <Text variant="bodySecondary">
                            {t("guardrails.modal.requiredMissing")}
                          </Text>
                        </>
                      ) : (
                        <>
                          <div className="flex items-center gap-1">
                            <div className="flex items-center gap-2">
                              <Text variant="smBodySecondary">
                                {t("guardrails.modal.yourScore")}:
                              </Text>
                              <DealScoreBadge
                                scoreText={questionnaire.score}
                                scoreType={questionnaire.kpi_value as ScoreType}
                                size="sm"
                              />
                            </div>
                            <div className="flex items-center gap-2">
                              <IconContainer size="5" color="secondary">
                                <ArrowRight />
                              </IconContainer>
                              <Text variant="smBodySecondary">
                                {t("guardrails.modal.required")}:
                              </Text>
                              <DealScoreBadge
                                scoreText={t("guardrails.modal.pass")}
                                scoreType={questionnaire.kpi_value as ScoreType}
                                size="sm"
                              />
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  )
                )}
              </div>

              <Text as="div" variant="body" className="text-center">
                {t("guardrails.modal.updateMessage")}
              </Text>
            </>
          )}
        </div>
      }
      footer={
        <div
          className="flex justify-center gap-2 mt-4"
          data-test="deals-guardrails-modal-footer"
        >
          <Button
            data-test="deals-guardrails-modal-cancel-button"
            variant="secondary"
            onClick={handleCancel}
          >
            {t("cancel")}
          </Button>
          <Button
            data-test="deals-guardrails-modal-update-button"
            onClick={handleUpdateWorkflows}
          >
            {isCompletedWorkflowsErrorFlag && nextStage
              ? t("guardrails.modal.proceedToStage", {
                  stageName: nextStage.name
                })
              : t("guardrails.modal.updateWorkflows")}
          </Button>
        </div>
      }
      hideDivider={true}
    />
  );
};

export default GuardrailsFailureModal;
